import { Row, Col, Typography, Select, InputNumber, Space } from "antd";
import type { GaugeScaleConfig, GaugeScaleType } from "../../Processing/types";
import { GAUGE_SCALE_TYPES, SCALE_TYPE_LABELS } from "../../Processing/gaugeScaleTypes";

const { Option } = Select;

/** Shared width so Scale Min, Scale Max, and Custom Max line up. */
const CONTROL_WIDTH = 150;

interface GaugeScaleFormProps {
  value: GaugeScaleConfig;
  onChange: (config: GaugeScaleConfig) => void;
  isGpsMetric: boolean;
  /** Player-scoped scale options are only enabled when exactly one player is selected. */
  hasSinglePlayer: boolean;
  /** Position-scoped scale options are only enabled when a position is selected. */
  hasPosition: boolean;
  /** False for a metric with no best, such as a helmet count: the save fails on a `*_best` scale. */
  hasBest?: boolean;
}

export function GaugeScaleForm({
  value,
  onChange,
  isGpsMetric,
  hasSinglePlayer,
  hasPosition,
  hasBest = true,
}: GaugeScaleFormProps) {
  const isTypeDisabled = (t: GaugeScaleType): boolean => {
    if (!hasBest && (t === "player_best" || t === "position_best" || t === "team_best")) return true;
    if (t === "player_avg" || t === "player_best" || t === "player_sum") return !hasSinglePlayer;
    // Position scale resolves from a selected position, or from a single player's position.
    if (t === "position_avg" || t === "position_best" || t === "position_sum")
      return !hasPosition && !hasSinglePlayer;
    if (t === "baseline_multiplier") return !isGpsMetric;
    return false;
  };

  // Custom is the only scale type with a concrete max at config time; the rest resolve from data.
  const customMaxInvalid =
    value.type === "custom" && value.customMax != null && value.min >= value.customMax;

  return (
    <Space direction="vertical" size="middle" style={{ width: "100%" }}>
      <Row align="middle" gutter={8}>
        <Col span={8}><Typography.Text>Scale Min</Typography.Text></Col>
        <Col span={16}>
          <InputNumber
            size="small"
            value={value.min}
            onChange={(v) => onChange({ ...value, min: v ?? 0 })}
            status={customMaxInvalid ? "error" : undefined}
            style={{ width: CONTROL_WIDTH }}
          />
        </Col>
      </Row>

      <Row align="middle" gutter={8}>
        <Col span={8}><Typography.Text>Scale Max</Typography.Text></Col>
        <Col span={16}>
          <Space size={6} align="center">
            <Select
              size="small"
              value={value.type}
              onChange={(type: GaugeScaleType) =>
                onChange({
                  ...value,
                  type,
                  multiplier: 1.0,
                  customMax: null,
                })
              }
              style={{ width: CONTROL_WIDTH }}
            >
              {GAUGE_SCALE_TYPES.map((t) => (
                <Option key={t} value={t} disabled={isTypeDisabled(t)}>
                  {SCALE_TYPE_LABELS[t]}
                </Option>
              ))}
            </Select>
            {value.type === "baseline_multiplier" && (
              <>
                <Typography.Text>×</Typography.Text>
                <InputNumber
                  size="small"
                  min={0.1}
                  step={0.1}
                  precision={2}
                  value={value.multiplier}
                  onChange={(v) => onChange({ ...value, multiplier: v ?? 1.0 })}
                  style={{ width: 70 }}
                />
              </>
            )}
          </Space>
        </Col>
      </Row>

      {value.type === "custom" && (
        <Row gutter={8}>
          <Col span={8}><Typography.Text>Max value</Typography.Text></Col>
          <Col span={16}>
            <InputNumber
              size="small"
              value={value.customMax ?? undefined}
              onChange={(v) => onChange({ ...value, customMax: v ?? null })}
              status={customMaxInvalid ? "error" : undefined}
              style={{ width: CONTROL_WIDTH }}
            />
            {customMaxInvalid && (
              <Typography.Text type="danger" style={{ display: "block", fontSize: 12, marginTop: 4 }}>
                Max value must be greater than scale min.
              </Typography.Text>
            )}
          </Col>
        </Row>
      )}
    </Space>
  );
}
